import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';
import VoiceLab from './VoiceLab';

const { FiArrowLeft, FiArrowRight, FiEdit3, FiShield, FiFeather, FiCheckCircle } = FiIcons;

const STEPS = [
  { icon: FiEdit3, title: 'Paste your words', body: 'Drop in up to 5,000 characters of anything you’ve written — an email, a paragraph, a rough scene.' },
  { icon: FiFeather, title: 'Rewrite in a voice', body: 'Watch it rewritten in a voice we studied from a real book — Austen, Tolstoy, Wilde, Carroll, Dickens.' },
  { icon: FiShield, title: 'Prove it’s real', body: 'Run our AI analyzer: see how human it reads and how closely it matches that voice, sentence by sentence.' },
];

const TryPage = () => {
  const structuredData = {
    '@context': 'https://schema.org', '@type': 'WebApplication',
    name: 'ManuscriptSmith Voice Lab',
    applicationCategory: 'WritingApplication',
    operatingSystem: 'Web',
    description: 'Rewrite your writing in any author’s voice and analyze it with AI to see if it reads human — live demo, no signup.',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="Try the Demo — Write in Any Author’s Voice | ManuscriptSmith"
        description="Paste your writing, rewrite it in a famous author’s voice, and run an AI analyzer to see if it reads human. Live demo, no signup. Then train it on your own voice."
        keywords="write in an author's voice, AI that writes in my voice, rewrite my text in an author's style, does my writing sound like AI, AI author voice generator, AI detection for writers"
        canonicalUrl="https://manuscriptsmith.com/try"
        structuredData={structuredData}
      />

      <main>
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-2 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-secondary-700 bg-secondary-50 px-3 py-1 rounded-full mb-4">
              <SafeIcon icon={FiFeather} className="w-4 h-4" /> Live demo · no signup · real AI
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Write it in any author’s <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">voice</span> — right now
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Paste your own words, rewrite them in a voice we learned from a real book, then let our AI prove it reads
              human. It’s the same engine that, in the real studio, learns <em>your</em> voice.
            </p>
          </motion.div>
        </section>

        {/* The tool */}
        <VoiceLab />

        {/* How it works */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.title} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center mb-3">
                  <SafeIcon icon={s.icon} className="w-5 h-5 text-white" />
                </div>
                <div className="text-xs font-bold text-primary-500 mb-1">STEP {i + 1}</div>
                <div className="font-bold text-gray-900 mb-1">{s.title}</div>
                <p className="text-sm text-gray-600">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-3">Now make it sound like <span className="underline decoration-white/40">you</span></h2>
            <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
              Those were famous authors. In the studio, ManuscriptSmith learns your voice from your own pages — so every
              chapter sounds like you wrote it, and passes AI detection. See plans for full access.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/signup" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary-700 font-bold text-lg rounded-lg hover:bg-gray-50">
                Train it on my voice <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </Link>
              <Link to="/analysis" className="inline-flex items-center justify-center px-6 py-4 bg-white/15 border border-white/40 text-white font-semibold text-lg rounded-lg hover:bg-white/25">
                See a full book analysis
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-5 mt-6 text-sm opacity-90">
              <span className="inline-flex items-center gap-1.5"><SafeIcon icon={FiCheckCircle} className="w-4 h-4" /> Your words stay yours</span>
              <span className="inline-flex items-center gap-1.5"><SafeIcon icon={FiCheckCircle} className="w-4 h-4" /> Passes AI detection</span>
              <span className="inline-flex items-center gap-1.5"><SafeIcon icon={FiCheckCircle} className="w-4 h-4" /> KDP-ready export</span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default TryPage;
